/* eslint-disable complexity */
import type { ComponentProps, ComponentType, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ArticleUnitPricingMeasure } from 'graphql-ts-types';
import { alignSelf, flexDirection, space } from 'styled-system';
import type { SpaceProps } from 'styled-system';

import type { ArticleCouponProps } from '../../../article-price/components/ArticlePrice';
import type { Replace } from '../../providers/LocaleProvider';
import { useLocalization } from '../../providers/LocaleProvider';
import type { RegularPrice, RRPPrice as RRPPriceProps, SpecialPrice } from '../../types/Article';
import type { ArticleLabelsList } from '../../types/labels';
import type { EnergyClassProps } from '../EnergyClass';
import EnergyClass from '../EnergyClass';
import { Flex } from '../Flex';
import { HeadlineH6, HeadlineH9 } from '../Typography';
import type { CommonTypographyProps } from '../Typography';
import ArticlePriceInfoIcon from './ArticlePriceInfoIcon';
import ArticlePriceOrder from './ArticlePriceOrder';
import {
  BasePrice,
  OldPrice,
  OldPriceLarger,
  oldPriceStyles,
  priceStyles,
} from './BasePriceComponents';
import SaleLabel from './SaleLabel';

export enum RenderingStrategy {
  Horizontal,
  Vertical,
}

interface BasePriceParams {
  regularPrice: Nullable<RegularPrice>;
  specialPrice?: Nullable<Pick<SpecialPrice, 'value'>>;
  fc: (value: number) => string;
  r: Replace;
}

export interface ArticleSmallPriceProps {
  energyClass?: Nullable<EnergyClassProps>;
  regularPrice?: Nullable<RegularPrice>;
  rrpPrice?: Nullable<Pick<RRPPriceProps, 'value'>>;
  specialPrice?: Nullable<Pick<SpecialPrice, 'value'>>;
  energyClassHref?: string;
  renderingStrategy?: RenderingStrategy;
  EcoMobilierLabel?: Nullable<ReactNode>;
  measurement?: Nullable<ArticleUnitPricingMeasure>;
  isOrdered?: boolean;
  className?: string;
  priceComponent?: ComponentType<CommonTypographyProps>;
  withSpecialAndRRP?: boolean;
  withInfoIcon?: boolean;
  infoIconSize?: number;
  discountedPrice?: number | null;
  coupon?: Nullable<Pick<ArticleCouponProps, 'priceAfterDiscount'>>;
  hasValuePackLabel?: boolean;
  withLargerEnergyClass?: boolean;
  withMarketingRed?: boolean;
  withLargerOldPrice?: boolean;
  labels_v2?: Nullable<ArticleLabelsList>;
  isV2?: boolean;
}

const HorizontalPriceContainer = styled.div``;

const VerticalPriceContainer = styled(Flex)``;

const verticalPriceContainerStyles: ComponentProps<typeof VerticalPriceContainer> = {
  justifyContent: 'center',
  flexWrap: 'wrap',
  alignItems: 'center',
};

const VerticalEELPriceContainer = styled(Flex)`
  ${flexDirection};

  > span {
    ${alignSelf};
  }
`;

const verticalEELPriceContainerStyles: ComponentProps<typeof VerticalPriceContainer> = {
  flexDirection: 'column',
  alignSelf: 'flex-start',
};

const EnergyClassContainer = styled.div<SpaceProps>`
  ${space};
`;

const energyClassContainerStyles: ComponentProps<typeof EnergyClassContainer> = {
  mr: ['sp_4', null, null, 'sp_8'],
};

const EnergyClassLargerContainer = styled(HeadlineH6)<typeof EnergyClassContainer>`
  ${space};
`;

const getPriceContainer = (isHorizontal: boolean, hasEnergyClass: boolean) => {
  if (isHorizontal) {
    return HorizontalPriceContainer;
  }

  if (hasEnergyClass) {
    return VerticalEELPriceContainer;
  }

  return VerticalPriceContainer;
};

const getPriceContainerStyles = (isHorizontal: boolean, hasEnergyClass: boolean) => {
  if (isHorizontal) {
    return {};
  }

  if (hasEnergyClass) {
    return verticalEELPriceContainerStyles;
  }

  return verticalPriceContainerStyles;
};

const getEnergyClassContainer = (withLargerEnergyClass: boolean) =>
  withLargerEnergyClass ? EnergyClassLargerContainer : EnergyClassContainer;

const renderCurrentPrice = ({
  currentPrice,
  isOnSale,
  PriceComponent,
  fc,
  r,
  withMarketingRed,
}: Pick<BasePriceParams, 'fc' | 'r'> & {
  currentPrice: Nullable<RegularPrice>;
  isOnSale: boolean;
  PriceComponent: ComponentType<CommonTypographyProps>;
  withMarketingRed?: boolean;
}) => {
  if (!currentPrice) return null;

  return (
    <BasePrice
      isOnSale={isOnSale}
      withMarketingRed={withMarketingRed}
      data-testid="current-price"
      {...priceStyles}
      aria-label={
        isOnSale
          ? r.t('h24_sale_price_aria_label', { price: fc(currentPrice.value) })
          : r.t('h24_black_price_aria_label', { price: fc(currentPrice.value) })
      }
    >
      <PriceComponent as="span" whiteSpace="nowrap" color="inherit" id="current-price">
        {fc(currentPrice.value)}
      </PriceComponent>
    </BasePrice>
  );
};

const renderOldPrice = ({
  regularPrice,
  fc,
  withLargerOldPrice,
}: Pick<BasePriceParams, 'regularPrice' | 'fc'> & { withLargerOldPrice?: boolean }) => {
  if (!regularPrice) return null;

  const OldPriceComponent = withLargerOldPrice ? OldPriceLarger : OldPrice;

  return (
    <OldPriceComponent {...oldPriceStyles} data-testid="old-price">
      <span data-testid="old-price-amount">{fc(regularPrice.value)}</span>
    </OldPriceComponent>
  );
};

const renderInfoIcon = ({
  regularPrice,
  specialPrice,
  discountedPrice,
  coupon,
  hasValuePackLabel = false,
  iconSize,
}: {
  regularPrice: Nullable<RegularPrice>;
  specialPrice: Nullable<Pick<SpecialPrice, 'value'>>;
  discountedPrice?: number | null;
  coupon?: Nullable<Pick<ArticleCouponProps, 'priceAfterDiscount'>>;
  hasValuePackLabel?: boolean;
  iconSize?: number;
}) => (
  <ArticlePriceInfoIcon
    iconSize={iconSize}
    regularPrice={regularPrice ? { value: regularPrice.value, isRrp: regularPrice.isRrp } : null}
    specialPrice={specialPrice ? { value: specialPrice.value } : null}
    discountedPrice={discountedPrice}
    coupon={coupon}
    hasValuePackLabel={hasValuePackLabel}
  />
);

interface PriceContentProps extends Pick<BasePriceParams, 'regularPrice'> {
  currentPrice: Nullable<RegularPrice>;
  isOnSale: boolean;
  shouldDisplayRRP: boolean;
  measurement: Nullable<ArticleUnitPricingMeasure>;
  rrpPrice: Nullable<Pick<RRPPriceProps, 'value'>>;
  isOrdered?: boolean;
  EcoMobilierLabel: ReactNode;
  PriceComponent: ComponentType<CommonTypographyProps>;
  withMarketingRed?: boolean;
  withLargerOldPrice?: boolean;
  labels_v2?: ArticleSmallPriceProps['labels_v2'];
  isV2?: boolean;
}

const PriceContent: React.FC<PriceContentProps> = ({
  currentPrice,
  isOnSale,
  regularPrice,
  shouldDisplayRRP,
  measurement,
  rrpPrice,
  isOrdered,
  EcoMobilierLabel,
  PriceComponent,
  labels_v2,
  withMarketingRed,
  withLargerOldPrice,
  isV2 = false,
}) => {
  const { fc, r } = useLocalization();

  return (
    <Flex alignItems="center" flexWrap="wrap">
      {renderCurrentPrice({ currentPrice, isOnSale, PriceComponent, fc, r, withMarketingRed })}
      {isOnSale && renderOldPrice({ regularPrice, fc, withLargerOldPrice })}
      {labels_v2 && (
        <SaleLabel labels_v2={labels_v2} regularPrice={regularPrice?.value} isV2={isV2} />
      )}
      <ArticlePriceOrder
        shouldDisplayRRP={shouldDisplayRRP}
        measurement={measurement}
        rrpPrice={rrpPrice}
        isOrdered={isOrdered}
      />
      {EcoMobilierLabel}
    </Flex>
  );
};

const ArticleSmallPrice = ({
  energyClass,
  energyClassHref,
  specialPrice,
  regularPrice,
  rrpPrice,
  renderingStrategy,
  EcoMobilierLabel,
  measurement,
  isOrdered,
  className,
  priceComponent: PriceComponent = HeadlineH9,
  withSpecialAndRRP = false,
  withInfoIcon = false,
  infoIconSize,
  discountedPrice,
  coupon,
  hasValuePackLabel = false,
  withLargerEnergyClass = false,
  withMarketingRed = false,
  withLargerOldPrice = false,
  labels_v2,
  isV2 = false,
}: ArticleSmallPriceProps) => {
  const hasEnergyClass = !!energyClass;
  const isOnSale = specialPrice && regularPrice && specialPrice.value !== regularPrice.value;
  const currentPrice = isOnSale ? specialPrice : regularPrice;
  const isHorizontal = renderingStrategy === RenderingStrategy.Horizontal;
  const shouldDisplayRRP = withSpecialAndRRP ? !!rrpPrice : !specialPrice && !!rrpPrice;
  const PriceContainer = getPriceContainer(isHorizontal, hasEnergyClass);
  const priceContainerStyles = getPriceContainerStyles(isHorizontal, hasEnergyClass);
  const shouldShowInfoIcon = withInfoIcon && (isOnSale || !!coupon);
  const EnergyContainerComponent = getEnergyClassContainer(withLargerEnergyClass);

  return (
    <Flex className={className} alignItems="center">
      {hasEnergyClass && (
        <EnergyContainerComponent {...energyClassContainerStyles}>
          <EnergyClass {...energyClass} href={energyClassHref} />
        </EnergyContainerComponent>
      )}
      <PriceContainer {...priceContainerStyles}>
        <PriceContent
          currentPrice={currentPrice}
          isOnSale={!!isOnSale}
          regularPrice={regularPrice}
          shouldDisplayRRP={shouldDisplayRRP}
          measurement={measurement}
          rrpPrice={rrpPrice}
          isOrdered={isOrdered}
          EcoMobilierLabel={EcoMobilierLabel}
          PriceComponent={PriceComponent}
          labels_v2={labels_v2}
          withMarketingRed={withMarketingRed}
          withLargerOldPrice={withLargerOldPrice}
          isV2={isV2}
        />
      </PriceContainer>
      {shouldShowInfoIcon &&
        renderInfoIcon({
          regularPrice,
          specialPrice,
          discountedPrice,
          coupon,
          hasValuePackLabel,
          iconSize: infoIconSize,
        })}
    </Flex>
  );
};

export default ArticleSmallPrice;
